/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable import/no-cycle */

import React, { useState } from 'react';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';

import { SCHEMA } from '../../../constants';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import FormWrapper from '../../theme/FormWrapper';
import FormDesc from '../../theme/FormDesc';
import FormElement from '../../theme/FormElement';
import FormActions from '../../theme/FormActions';
import ModalPwdResetEmailSent from '../../theme/SimpleModal/ModalPwdResetEmailSent';
import ErrorBanner from '../../theme/ErrorBanner';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import InputValidator from '../../../services/input-validator';
import Logger from '../../../services/logger';

const gaScreenName = 'Forgot Password';

const FormContent = (props) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
    initialValues,
    validateForm,
  } = props;

  const { goPrev } = useFlowControl();

  const { email } = values;
  const { email: emailInitial } = initialValues;

  const showErrorFor = (name) => touched[name] && Boolean(errors[name]);
  const showEmailError = showErrorFor('email');

  const isSubmitButtonDisabled = React.useMemo(() => {
    // making dirty to true if initial email is provided.
    const isDirty = emailInitial ? true : dirty;
    return !isDirty || !isValid;
  }, [dirty, isValid, emailInitial]);

  React.useEffect(() => {
    // trigger form validation if initial email is provided.
    if (emailInitial) {
      validateForm();
    }
  }, [emailInitial, validateForm]);

  const handleCancel = () => {
    goPrev();
  };

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled],
  );

  return (
    <FormWrapper>
      <FormElement>
        <Input
          id="forgotInputEmail"
          data-testid="forgotInputEmail"
          type="email"
          name="email"
          label="Email"
          value={email}
          helpText={showEmailError ? errors.email : ''}
          error={showEmailError}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="email"
        />
      </FormElement>
      <FormActions multipleActions>
        <ButtonContained
          compact
          disabled={isSubmitButtonDisabled}
          loading={isSubmitting}
          onClick={submitForm}
          data-testid="forgotButtonSubmit"
        >
          Send reset link
        </ButtonContained>
        <ButtonContained
          color="grey"
          compact
          onClick={handleCancel}
          data-testid="forgotButtonCancel"
        >
          Back to log in
        </ButtonContained>
      </FormActions>
    </FormWrapper>
  );
};

const ForgotSchema = YupObject().shape({
  email: InputValidator.emailValidation(SCHEMA.email),
});

const PasswordResetLink = () => {
  const { forgotPassword, userContext, trackPwl } = useAuth0();
  const { goPrev } = useFlowControl();

  const [isPwdResetEmailSent, setIsPwdResetEmailSent] = React.useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Landed',
      event_label: 'Forgot_Password',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userEmail = userContext.email || '';

  const backToLogIn = React.useCallback(() => {
    goPrev();
  }, [goPrev]);

  const closeAndGoBack = React.useCallback(() => {
    setIsPwdResetEmailSent(false);
    backToLogIn();
  }, [backToLogIn]);

  const initialValues = {
    email: userEmail,
  };

  const initialTouched = { email: Boolean(userEmail) };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Click',
      event_label: 'Forgot_Password_Reset',
    });

    const emailObj = { email: values.email };

    setError(null);

    await new Promise((resolve) => {
      forgotPassword(emailObj, (err) => {
        setSubmitting(false);

        if (err) {
          setError(err);
          Logger.error(err);
          return;
        }
        resolve();

        setIsPwdResetEmailSent(true);
        resetForm();
      });
    });
  };

  return (
    <>
      <BackButtonDesktop action={backToLogIn} />
      <PageContent>
        <ModalPwdResetEmailSent open={isPwdResetEmailSent} ctaAction={closeAndGoBack} />
        <PageHeader header="Password reset link" backAction={backToLogIn} />
        <FormDesc>Enter your email and we’ll send you a link to reset your password.</FormDesc>
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={ForgotSchema}
          initialTouched={initialTouched}
          enableReinitialize
        >
          {(formikProps) => <FormContent {...formikProps} initialValues={initialValues} />}
        </Formik>
      </PageContent>
    </>
  );
};

export default PasswordResetLink;
